import { useStripe } from '@stripe/react-stripe-js';
import { FormEvent, useState } from 'react';
import { toast } from 'react-toastify';
import { Button } from '../Button/Button';
import { UserCardInterface } from '@app/models/user';
import VisaLogo from '@app/assets/images/visa.svg';
import MastercardLogo from '@app/assets/images/mastercard.svg';
import AmexLogo from '@app/assets/images/amex.svg';

const detectCardBrand = (cardNumber: string): string => {
  const digits = cardNumber.replace(/\s/g, '');
  if (!digits) return '';
  if (/^4/.test(digits)) return 'visa';
  if (/^5[1-5]/.test(digits)) return 'mastercard';
  if (/^3[47]/.test(digits)) return 'amex';
  if (/^6(?:011|5)/.test(digits)) return 'discover';
  return '';
};

const formatCardNumber = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 16);
  return digits.replace(/(\d{4})(?=\d)/g, '$1 ');
};

const CardBrandLogo = ({ brand }: { brand: string }) => {
  const lower = brand.toLowerCase();
  const logo =
    lower === 'visa'
      ? VisaLogo
      : lower === 'mastercard'
        ? MastercardLogo
        : lower === 'amex' || lower === 'american express'
          ? AmexLogo
          : null;

  return (
    <div className="h-8 flex items-center">
      {logo ? (
        <img src={logo} alt={brand} className="h-8 w-auto" />
      ) : (
        <span className="text-xs font-bold uppercase text-white/80">
          {brand || 'CARD'}
        </span>
      )}
    </div>
  );
};

interface CardFormData {
  cardNumber: string;
  name: string;
  expiryMonth: string;
  expiryYear: string;
  cvv: string;
}

interface AddModeProps {
  clientSecret: string;
  cancelAction: () => void;
  onSuccess?: () => void;
  editCard?: never;
  onSubmit?: never;
  isLoading?: never;
}

interface EditModeProps {
  editCard: UserCardInterface;
  cancelAction: () => void;
  onSubmit: (data: { name: string; exp_month: number; exp_year: number }) => void;
  isLoading?: boolean;
  clientSecret?: never;
  onSuccess?: never;
}

type PaymentCardFormProps = AddModeProps | EditModeProps;

export const PaymentCardForm = (props: PaymentCardFormProps) => {
  const { editCard } = props;

  if (editCard) {
    return <EditForm {...(props as EditModeProps)} />;
  }

  return <AddForm {...(props as AddModeProps)} />;
};

const AddForm = ({
  clientSecret,
  cancelAction,
  onSuccess,
}: AddModeProps) => {
  const stripe = useStripe();

  const [formData, setFormData] = useState<CardFormData>({
    cardNumber: '',
    name: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
  });
  const [errors, setErrors] = useState<
    Partial<Record<keyof CardFormData, string>>
  >({});
  const [submitError, setSubmitError] = useState<string | undefined>();
  const [isProcessing, setIsProcessing] = useState(false);

  const months = Array.from({ length: 12 }, (_, i) => ({
    value: String(i + 1).padStart(2, '0'),
    label: String(i + 1).padStart(2, '0'),
  }));

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 16 }, (_, i) => ({
    value: String(currentYear + i),
    label: String(currentYear + i),
  }));

  const handleCardNumberChange = (value: string) => {
    setFormData((prev) => ({ ...prev, cardNumber: formatCardNumber(value) }));
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof CardFormData, string>> = {};
    const digits = formData.cardNumber.replace(/\s/g, '');

    if (!digits || digits.length < 13) {
      newErrors.cardNumber = 'Enter a valid card number';
    }
    if (!formData.name.trim()) {
      newErrors.name = 'Enter the name on card';
    }
    if (!formData.expiryMonth) {
      newErrors.expiryMonth = 'Select month';
    }
    if (!formData.expiryYear) {
      newErrors.expiryYear = 'Select year';
    }
    if (!formData.cvv.match(/^\d{3}$/)) {
      newErrors.cvv = 'Enter a valid security code';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!stripe) return;
    if (!validate()) return;

    setIsProcessing(true);
    setSubmitError(undefined);

    const digits = formData.cardNumber.replace(/\s/g, '');

    // Create PaymentMethod via Stripe API (card data goes directly to Stripe, never to our server)
    const res = await fetch('https://api.stripe.com/v1/payment_methods', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${import.meta.env.VITE_APP_STRIPE_KEY}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        type: 'card',
        'card[number]': digits,
        'card[exp_month]': formData.expiryMonth,
        'card[exp_year]': formData.expiryYear,
        'card[cvc]': formData.cvv,
        'billing_details[name]': formData.name.trim(),
      }),
    });

    const pm = await res.json();
    if (pm.error) {
      setSubmitError(pm.error.message);
      setIsProcessing(false);
      return;
    }

    // Confirm SetupIntent with the created PaymentMethod
    const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
      payment_method: pm.id,
    });

    if (error) {
      setSubmitError(error.message);
      setIsProcessing(false);
      return;
    }

    if (setupIntent?.status === 'succeeded') {
      toast.success('Payment method saved successfully');
      setIsProcessing(false);
      if (onSuccess) {
        onSuccess();
      } else {
        cancelAction();
      }
    }
  };

  const liveBrand = detectCardBrand(formData.cardNumber);

  const previewNumber = (() => {
    const digits = formData.cardNumber.replace(/\s/g, '');
    const padded = digits.padEnd(16, '\u2022');
    return `${padded.slice(0, 4)} \u2013 ${padded.slice(4, 8)} \u2013 ${padded.slice(8, 12)} \u2013 ${padded.slice(12, 16)}`;
  })();

  return (
    <form onSubmit={handleSubmit} className="pt-6">
      <div className="grid grid-cols-2 gap-x-4 gap-y-4 mb-10">
        {/* Card number */}
        <div className="w-full">
          <label className="font-medium text-sm inline-block mb-1">
            Card number
          </label>
          <input
            type="text"
            value={formData.cardNumber}
            onChange={(e) => handleCardNumberChange(e.target.value)}
            maxLength={19}
            className={`bg-base-seasalt rounded py-3 px-4 w-full outline-brand-baseBlue outline-1 ${
              errors.cardNumber ? 'border border-system-error' : ''
            }`}
          />
          {errors.cardNumber && (
            <p role="alert" className="text-system-error text-sm text-left pt-1">
              {errors.cardNumber}
            </p>
          )}
        </div>

        {/* Name on card */}
        <div className="w-full">
          <label className="font-medium text-sm inline-block mb-1">
            Name on card
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, name: e.target.value }))
            }
            className={`bg-base-seasalt rounded py-3 px-4 w-full outline-brand-baseBlue outline-1 ${
              errors.name ? 'border border-system-error' : ''
            }`}
          />
          {errors.name && (
            <p role="alert" className="text-system-error text-sm text-left pt-1">
              {errors.name}
            </p>
          )}
        </div>

        {/* Expiration date */}
        <div className="w-full">
          <label className="font-medium text-sm inline-block mb-1">
            Expiration date
          </label>
          <div className="flex gap-2">
            <select
              value={formData.expiryMonth}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  expiryMonth: e.target.value,
                }))
              }
              className={`flex-1 bg-base-seasalt rounded py-3 px-4 outline-brand-baseBlue outline-1 ${
                errors.expiryMonth ? 'border border-system-error' : ''
              }`}
            >
              <option value="">MM</option>
              {months.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
            <select
              value={formData.expiryYear}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  expiryYear: e.target.value,
                }))
              }
              className={`flex-1 bg-base-seasalt rounded py-3 px-4 outline-brand-baseBlue outline-1 ${
                errors.expiryYear ? 'border border-system-error' : ''
              }`}
            >
              <option value="">YYYY</option>
              {years.map((y) => (
                <option key={y.value} value={y.value}>
                  {y.label}
                </option>
              ))}
            </select>
          </div>
          {(errors.expiryMonth || errors.expiryYear) && (
            <p role="alert" className="text-system-error text-sm text-left pt-1">
              {errors.expiryMonth || errors.expiryYear}
            </p>
          )}
        </div>

        {/* Security Code */}
        <div className="w-full">
          <label className="font-medium text-sm inline-block mb-1">
            Security Code (CVV/CVC)
          </label>
          <input
            type="password"
            placeholder="***"
            value={formData.cvv}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, '').slice(0, 3);
              setFormData((prev) => ({ ...prev, cvv: val }));
            }}
            maxLength={3}
            className={`bg-base-seasalt rounded py-3 px-4 w-full outline-brand-baseBlue outline-1 ${
              errors.cvv ? 'border border-system-error' : ''
            }`}
          />
          {errors.cvv && (
            <p role="alert" className="text-system-error text-sm text-left pt-1">
              {errors.cvv}
            </p>
          )}
        </div>
      </div>

      {/* Card Preview */}
      <div className="flex justify-center pt-6 pb-12">
        <div className="bg-brand-darkBlue3 rounded-2xl max-w-[300px] w-full pb-6 pl-6 pt-4 pr-4 text-white">
          <div className="flex justify-between items-start mb-12">
            <span className="text-sm font-medium">Credit Card</span>
            <CardBrandLogo brand={liveBrand} />
          </div>
          <div className="text-base font-bold tracking-wide mb-6">
            {previewNumber}
          </div>
          <div className="flex justify-between items-end">
            <span className="text-sm">{formData.name || ''}</span>
            <span className="text-sm">
              {formData.expiryMonth && formData.expiryYear
                ? `${formData.expiryMonth}/${formData.expiryYear.slice(-2)}`
                : 'MM/YY'}
            </span>
          </div>
        </div>
      </div>

      {submitError && (
        <div className="text-system-error font-bold py-2">{submitError}</div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <Button
          kind="secondary"
          type="button"
          onClick={cancelAction}
          disabled={!stripe || isProcessing}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={!stripe || isProcessing}
          loading={isProcessing}
        >
          Add card
        </Button>
      </div>
    </form>
  );
};

const EditForm = ({
  editCard,
  cancelAction,
  onSubmit,
  isLoading,
}: EditModeProps) => {
  const months = Array.from({ length: 12 }, (_, i) => ({
    value: String(i + 1).padStart(2, '0'),
    label: String(i + 1).padStart(2, '0'),
  }));

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 16 }, (_, i) => ({
    value: String(currentYear + i),
    label: String(currentYear + i),
  }));

  const [name, setName] = useState(editCard.name || '');
  const [expiryMonth, setExpiryMonth] = useState(
    String(editCard.expMonth).padStart(2, '0')
  );
  const [expiryYear, setExpiryYear] = useState(String(editCard.expYear));
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) {
      newErrors.name = 'Enter the name on card';
    }
    if (!expiryMonth) {
      newErrors.expiryMonth = 'Select month';
    }
    if (!expiryYear) {
      newErrors.expiryYear = 'Select year';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    onSubmit({
      name: name.trim(),
      exp_month: Number(expiryMonth),
      exp_year: Number(expiryYear),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="pt-6">
      <div className="grid grid-cols-2 gap-x-4 gap-y-4 mb-10">
        {/* Card number (read-only) */}
        <div className="w-full">
          <label className="font-medium text-sm inline-block mb-1">
            Card number
          </label>
          <input
            type="text"
            placeholder={`\u2022\u2022\u2022\u2022 \u2022\u2022\u2022\u2022 \u2022\u2022\u2022\u2022 ${editCard.last4}`}
            disabled
            className="bg-base-seasalt rounded py-3 px-4 w-full outline-brand-baseBlue outline-1 text-gray-400 cursor-not-allowed"
          />
        </div>

        {/* Name on card */}
        <div className="w-full">
          <label className="font-medium text-sm inline-block mb-1">
            Name on card
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={`bg-base-seasalt rounded py-3 px-4 w-full outline-brand-baseBlue outline-1 ${
              errors.name ? 'border border-system-error' : ''
            }`}
          />
          {errors.name && (
            <p role="alert" className="text-system-error text-sm text-left pt-1">
              {errors.name}
            </p>
          )}
        </div>

        {/* Expiration date */}
        <div className="w-full">
          <label className="font-medium text-sm inline-block mb-1">
            Expiration date
          </label>
          <div className="flex gap-2">
            <select
              value={expiryMonth}
              onChange={(e) => setExpiryMonth(e.target.value)}
              className={`flex-1 bg-base-seasalt rounded py-3 px-4 outline-brand-baseBlue outline-1 ${
                errors.expiryMonth ? 'border border-system-error' : ''
              }`}
            >
              <option value="">MM</option>
              {months.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
            <select
              value={expiryYear}
              onChange={(e) => setExpiryYear(e.target.value)}
              className={`flex-1 bg-base-seasalt rounded py-3 px-4 outline-brand-baseBlue outline-1 ${
                errors.expiryYear ? 'border border-system-error' : ''
              }`}
            >
              <option value="">YYYY</option>
              {years.map((y) => (
                <option key={y.value} value={y.value}>
                  {y.label}
                </option>
              ))}
            </select>
          </div>
          {(errors.expiryMonth || errors.expiryYear) && (
            <p role="alert" className="text-system-error text-sm text-left pt-1">
              {errors.expiryMonth || errors.expiryYear}
            </p>
          )}
        </div>
      </div>

      {/* Card Preview */}
      <div className="flex justify-center pt-6 pb-12">
        <div className="bg-brand-darkBlue3 rounded-2xl max-w-[300px] w-full pb-6 pl-6 pt-4 pr-4 text-white">
          <div className="flex justify-between items-start mb-12">
            <span className="text-sm font-medium">Credit Card</span>
            <CardBrandLogo brand={editCard.brand} />
          </div>
          <div className="text-base font-bold tracking-wide mb-6">
            {`\u2022\u2022\u2022\u2022 \u2013 \u2022\u2022\u2022\u2022 \u2013 \u2022\u2022\u2022\u2022 \u2013 ${editCard.last4}`}
          </div>
          <div className="flex justify-between items-end">
            <span className="text-sm">{name || ''}</span>
            <span className="text-sm">
              {expiryMonth && expiryYear
                ? `${expiryMonth}/${expiryYear.slice(-2)}`
                : 'MM/YY'}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Button kind="secondary" type="button" onClick={cancelAction} disabled={isLoading}>
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading} loading={isLoading}>
          Save changes
        </Button>
      </div>
    </form>
  );
};
